/* =============================================================
 * Luxanium Hexagon Product Customizer
 * - 3D rotating hexagon metal sheet (front/back mockups via schema)
 * - Multi-image upload queue with hexagon thumbnails
 * - Hexagon crop editor (drag / zoom / rotate) — Luxanium popup design
 * - Pre-cropped hexagon JPEG uploads to Cloudinary IMMEDIATELY on Done
 * - "Continue to Hexagon Wall" hands uploaded URLs to the builder
 *   via localStorage key: PendingCustomHexItems
 *
 * Requires THREE (r128) to be loaded before this file.
 * ============================================================= */
(function () {
  'use strict';

  var ACCEPTED = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/avif'];
  var HEIC_RE = /\.(heic|heif)$/i;
  var MAX_BYTES = 20 * 1024 * 1024;
  var HEX_RATIO = 1.1547;            /* height / width, pointy-top */
  var EXPORT_W = 1200;
  var EXPORT_H = Math.round(EXPORT_W * HEX_RATIO);
  var CLD_CAP = 9.5 * 1024 * 1024;   /* stay under Cloudinary's 10MB unsigned cap */
  var MIN_PRINT_PX = 1500;           /* below this = low-res flag */

  function clamp(v, min, max) { return Math.min(max, Math.max(min, v)); }

  /* pointy-top hexagon path on a canvas ctx, within box (0,0,w,h) */
  function hexPath(ctx, w, h) {
    ctx.beginPath();
    ctx.moveTo(w * 0.5, 0);
    ctx.lineTo(w, h * 0.25);
    ctx.lineTo(w, h * 0.75);
    ctx.lineTo(w * 0.5, h);
    ctx.lineTo(0, h * 0.75);
    ctx.lineTo(0, h * 0.25);
    ctx.closePath();
  }

  class LuxHexagonCustomizer extends HTMLElement {
    connectedCallback() {
      this.cfg = {
        cloudName: this.dataset.cloudName,
        uploadPreset: this.dataset.uploadPreset,
        frontMockup: this.dataset.mockupFront,
        backMockup: this.dataset.mockupBack,
        builderUrl: this.dataset.builderUrl || '/',
        unitPrice: parseFloat(this.dataset.unitPrice || '0'),
        currency: this.dataset.currency || '',
        totalNote: this.dataset.totalNote || ''
      };

      /* uploaded items:
         { id, img (original HTMLImageElement), transform {scale,x,y,rot},
           previewUrl (objectURL of cropped jpeg), croppedImg (HTMLImageElement),
           cloudUrl (secure_url | null), uploading, failed } */
      this.items = [];
      this.activeIndex = -1;
      this.editingIndex = -1;   /* item being edited; -1 = new pending file */
      this.pendingFiles = [];   /* files queued for the editor */
      this.pendingImg = null;

      /* DOM refs */
      this.stage      = this.querySelector('[data-lux-stage]');
      this.uploadBtn  = this.querySelector('[data-lux-upload]');
      this.editBtn    = this.querySelector('[data-lux-edit]');
      this.fileInput  = this.querySelector('[data-lux-file]');
      this.thumbsWrap = this.querySelector('[data-lux-thumbs]');
      this.thumbCount = this.querySelector('[data-lux-thumb-count]');
      this.continueBtn = this.querySelector('[data-lux-continue]');
      this.totalEl    = this.querySelector('[data-lux-total]');
      this.builderBtn  = this.querySelector('[data-lux-builder]');
      this.hint       = this.querySelector('[data-lux-hint]');
      this.editor     = this.querySelector('[data-lux-editor]');
      this.editorCanvas = this.querySelector('[data-lux-editor-canvas]');
      this.editorStep = this.querySelector('[data-lux-editor-step]');
      this.zoomSlider = this.querySelector('[data-lux-zoom]');

      this.transform = { scale: 1, x: 0, y: 0, rot: 0 };

      /* The upload panel + CTAs live inside the "Price + Upload + CTAs"
         block. If it isn't added in the theme editor, warn instead of
         crashing (a crash here would silently break the Upload button). */
      if (!this.thumbsWrap || !this.continueBtn || !this.editor) {
        console.error('[Lux Hexagon] Missing required elements. Add the ' +
          '"Price + Upload + CTAs" block to the section in the theme editor.');
        this.initThree();
        return;
      }

      this.bindEvents();
      this.initThree();
      this.renderThumbs();
    }

    disconnectedCallback() {
      if (this._raf) cancelAnimationFrame(this._raf);
    }

    /* ================= events ================= */
    bindEvents() {
      var self = this;

      this.uploadBtn.addEventListener('click', function () { self.fileInput.click(); });
      this.thumbsWrap.addEventListener('click', function (e) { self.onThumbsClick(e); });
      this.fileInput.addEventListener('change', function () {
        if (self.fileInput.files && self.fileInput.files[0]) {
          self.handleFile(self.fileInput.files[0]);
        }
        self.fileInput.value = '';
      });

      if (this.editBtn) {
        this.editBtn.addEventListener('click', function () {
          if (self.activeIndex >= 0) self.openEditorFor(self.activeIndex);
        });
      }

      this.continueBtn.addEventListener('click', function () { self.continueToWall(); });
      if (this.builderBtn) {
        this.builderBtn.addEventListener('click', function () {
          window.location.href = self.cfg.builderUrl;
        });
      }

      /* editor controls */
      this.querySelector('[data-lux-close]').addEventListener('click', function () { self.editorClose(); });
      this.querySelector('[data-lux-done]').addEventListener('click', function () { self.editorDone(); });
      this.querySelector('[data-lux-rotate]').addEventListener('click', function () {
        self.transform.rot = (self.transform.rot + 90) % 360;
        self.clampTransform();
        self.drawEditor();
      });
      this.querySelector('[data-lux-remove]').addEventListener('click', function () { self.editorRemove(); });

      this.zoomSlider.addEventListener('input', function () {
        self.transform.scale = parseFloat(self.zoomSlider.value);
        self.clampTransform();
        self.drawEditor();
      });

      /* editor canvas drag */
      var drag = null;
      this.editorCanvas.addEventListener('pointerdown', function (e) {
        e.preventDefault();
        drag = { x: e.clientX, y: e.clientY, tx: self.transform.x, ty: self.transform.y };
        self.editorCanvas.setPointerCapture(e.pointerId);
      });
      this.editorCanvas.addEventListener('pointermove', function (e) {
        if (!drag) return;
        var w = self.editorCanvas.width, h = self.editorCanvas.height;
        self.transform.x = drag.tx + (e.clientX - drag.x) / w;
        self.transform.y = drag.ty + (e.clientY - drag.y) / h;
        self.clampTransform();
        self.drawEditor();
      });
      this.editorCanvas.addEventListener('pointerup', function () { drag = null; });
      this.editorCanvas.addEventListener('pointercancel', function () { drag = null; });

      window.addEventListener('resize', function () { self.onResize(); });
    }

    /* ================= file intake (one image at a time) ================= */
    handleFile(file) {
      if (HEIC_RE.test(file.name) || file.type === 'image/heic' || file.type === 'image/heif') {
        this.showHint('iPhone HEIC photos aren\'t supported. On your iPhone go to Settings → Camera → Formats and choose "Most Compatible", or export the photo as JPEG and try again.');
        return;
      }
      if (ACCEPTED.indexOf(file.type) === -1) {
        this.showHint('Please upload a PNG, JPG or WebP image.');
        return;
      }
      if (file.size > MAX_BYTES) {
        this.showHint('Maximum file size is 20MB. Please upload a smaller image.');
        return;
      }
      this.showHint('');
      var self = this;
      this.pendingFile = file;
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          self.pendingImg = img;
          self.pendingDims = img.naturalWidth + 'x' + img.naturalHeight;
          self.pendingLowRes = (img.naturalWidth < MIN_PRINT_PX || img.naturalHeight < MIN_PRINT_PX);
          self.editingIndex = -1;
          self.transform = { scale: 1, x: 0, y: 0, rot: 0 };
          self.openEditor();
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    }

    /* Cloudinary unsigned uploads cap at 10MB. Files at/above ~9.5MB are
       re-encoded as JPEG, stepping quality down until they fit. Quality-first:
       dimensions are preserved unless quality alone can't get under the cap. */
    prepareForUpload(file, done) {
      if (file.size < CLD_CAP) { done(file); return; }
      var reader = new FileReader();
      reader.onload = function (e) {
        var img = new Image();
        img.onload = function () {
          var qualities = [0.92, 0.85, 0.8, 0.75, 0.7, 0.6];
          var qi = 0, scale = 1;
          function render() {
            var w = Math.round(img.naturalWidth * scale);
            var h = Math.round(img.naturalHeight * scale);
            var c = document.createElement('canvas');
            c.width = w; c.height = h;
            c.getContext('2d').drawImage(img, 0, 0, w, h);
            c.toBlob(function (blob) {
              if (!blob) { done(file); return; }
              if (blob.size < CLD_CAP) { done(blob); return; }
              qi++;
              if (qi < qualities.length) { render(); return; }
              if (scale > 0.45) { scale *= 0.8; qi = 0; render(); return; }
              done(blob);
            }, 'image/jpeg', qualities[qi]);
          }
          render();
        };
        img.onerror = function () { done(file); };
        img.src = e.target.result;
      };
      reader.onerror = function () { done(file); };
      reader.readAsDataURL(file);
    }

    /* XHR upload so we can report progress */
    uploadBlobXHR(blob, filename, onProgress, cb) {
      var fd = new FormData();
      fd.append('file', blob, filename);
      fd.append('upload_preset', this.cfg.uploadPreset);
      var xhr = new XMLHttpRequest();
      xhr.open('POST', 'https://api.cloudinary.com/v1_1/' + this.cfg.cloudName + '/image/upload', true);
      xhr.upload.onprogress = function (e) {
        if (e.lengthComputable && onProgress) onProgress(Math.round((e.loaded / e.total) * 100));
      };
      xhr.onload = function () {
        var data = null;
        try { data = JSON.parse(xhr.responseText); } catch (err) {}
        cb(data && data.secure_url ? data.secure_url : null);
      };
      xhr.onerror = function () { cb(null); };
      xhr.send(fd);
    }

    setUploadProgress(pct, label) {
      var el = this.querySelector('[data-lux-progress]');
      if (!el) return;
      if (pct === null) { el.hidden = true; el.textContent = ''; return; }
      el.hidden = false;
      if (pct === 'prep') { el.textContent = (label || 'Preparing') + '…'; return; }
      el.textContent = pct >= 100 ? 'Processing…' : (label || 'Uploading') + ' ' + pct + '%';
    }

    /* ================= crop editor ================= */
    currentEditorImage() {
      if (this.editingIndex >= 0) return this.items[this.editingIndex].img;
      return this.pendingImg;
    }

    openEditor() {
      this.editor.hidden = false;
      document.body.classList.add('lux-no-scroll');
      this.zoomSlider.value = this.transform.scale;
      this.updateEditorStep();
      this.drawEditor();
    }

    openEditorFor(index) {
      var it = this.items[index];
      if (!it) return;
      this.editingIndex = index;
      this.pendingImg = null;
      this.transform = {
        scale: it.transform.scale, x: it.transform.x,
        y: it.transform.y, rot: it.transform.rot
      };
      this.openEditor();
    }

    updateEditorStep() {
      if (!this.editorStep) return;
      if (this.editingIndex >= 0) {
        this.editorStep.textContent = 'Editing image ' + (this.editingIndex + 1);
      } else {
        this.editorStep.textContent = 'New image';
      }
    }

    editorClose() {
      this.editor.hidden = true;
      document.body.classList.remove('lux-no-scroll');
      this.pendingImg = null;
      this.pendingFile = null;
      this.editingIndex = -1;
    }

    editorRemove() {
      if (this.editingIndex >= 0) {
        this.removeItem(this.editingIndex);
        this.editorClose();
      } else {
        this.editorClose();
      }
    }

    drawEditor() {
      var img = this.currentEditorImage();
      var c = this.editorCanvas;
      var dispH = Math.min(460, window.innerHeight * 0.55);
      var dispW = Math.round(dispH / HEX_RATIO);
      var maxW = Math.floor(window.innerWidth * 0.86);
      if (dispW > maxW) {
        dispW = maxW;
        dispH = Math.round(dispW * HEX_RATIO);
      }
      c.width = dispW; c.height = dispH;
      /* inline display size — wins over any theme stylesheet rule */
      c.style.width = dispW + 'px';
      c.style.height = dispH + 'px';
      var ctx = c.getContext('2d');

      ctx.fillStyle = '#0d0d0f';
      ctx.fillRect(0, 0, dispW, dispH);

      if (img) {
        /* outside the hexagon: blurred + slightly dimmed */
        ctx.save();
        ctx.filter = 'blur(14px)';
        this.drawPhoto(ctx, dispW, dispH, img);
        ctx.restore();
        ctx.fillStyle = 'rgba(13,13,15,0.45)';
        ctx.fillRect(0, 0, dispW, dispH);

        /* inside the hexagon: sharp */
        ctx.save();
        hexPath(ctx, dispW, dispH);
        ctx.clip();
        this.drawPhoto(ctx, dispW, dispH, img);
        ctx.restore();
      }

      hexPath(ctx, dispW, dispH);
      ctx.strokeStyle = 'rgba(203,180,137,0.95)';
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    /* same math family as lux-product-customizer: cover * zoom,
       normalized offsets (fractions of viewport), 90° rotations */
    drawPhoto(ctx, w, h, img) {
      var rot = this.transform.rot * Math.PI / 180;
      var rw = (this.transform.rot % 180 === 0) ? img.naturalWidth : img.naturalHeight;
      var rh = (this.transform.rot % 180 === 0) ? img.naturalHeight : img.naturalWidth;
      var cover = Math.max(w / rw, h / rh);
      var eff = cover * this.transform.scale;

      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, w, h);
      ctx.clip();
      ctx.translate(w / 2 + this.transform.x * w, h / 2 + this.transform.y * h);
      ctx.rotate(rot);
      ctx.scale(eff, eff);
      ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
      ctx.restore();
    }

    clampTransform() {
      var img = this.currentEditorImage();
      if (!img) return;
      var ratio = 1 / HEX_RATIO;    /* w / h of viewport */
      var rw = (this.transform.rot % 180 === 0) ? img.naturalWidth : img.naturalHeight;
      var rh = (this.transform.rot % 180 === 0) ? img.naturalHeight : img.naturalWidth;
      var imgRatio = rw / rh;
      var scale = this.transform.scale;
      var iwOverW = scale * Math.max(1, imgRatio / ratio);
      var ihOverH = scale * Math.max(1, ratio / imgRatio);
      var maxX = Math.max(0, (iwOverW - 1) / 2);
      var maxY = Math.max(0, (ihOverH - 1) / 2);
      this.transform.x = clamp(this.transform.x, -maxX, maxX);
      this.transform.y = clamp(this.transform.y, -maxY, maxY);
    }

    editorDone() {
      var self = this;
      var img = this.currentEditorImage();
      if (!img) { this.editorClose(); return; }

      /* export the hexagon bounding crop */
      var c = document.createElement('canvas');
      c.width = EXPORT_W; c.height = EXPORT_H;
      var ctx = c.getContext('2d');
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, EXPORT_W, EXPORT_H);
      this.drawPhoto(ctx, EXPORT_W, EXPORT_H, img);

      c.toBlob(function (blob) {
        if (!blob) { self.showHint('Could not process image, please retry.'); return; }
        var previewUrl = URL.createObjectURL(blob);
        var croppedImg = new Image();
        croppedImg.onload = function () {
          if (self.editingIndex >= 0) {
            /* re-crop of an existing item — replace preview + re-upload */
            var it = self.items[self.editingIndex];
            if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
            it.transform = {
              scale: self.transform.scale, x: self.transform.x,
              y: self.transform.y, rot: self.transform.rot
            };
            it.previewUrl = previewUrl;
            it.croppedImg = croppedImg;
            it.cloudUrl = null;
            it.failed = false;
            self.setActive(self.editingIndex);
            self.uploadItem(it, blob);
            self.editorClose();
          } else {
            var item = {
              id: 'hex_' + Date.now().toString(36) + '_' + Math.floor(Math.random() * 1e4),
              img: img,
              transform: {
                scale: self.transform.scale, x: self.transform.x,
                y: self.transform.y, rot: self.transform.rot
              },
              previewUrl: previewUrl,
              croppedImg: croppedImg,
              cloudUrl: null,
              originalFile: self.pendingFile,
              originalUrl: null,
              dims: self.pendingDims || '',
              lowRes: !!self.pendingLowRes,
              uploading: false,
              failed: false
            };
            self.items.push(item);
            self.setActive(self.items.length - 1);
            self.uploadItem(item, blob);          /* upload immediately */
            self.pendingImg = null;
            self.pendingFile = null;
            self.editorClose();
          }
          self.renderThumbs();
        };
        croppedImg.src = previewUrl;
      }, 'image/jpeg', 0.92);
    }

    uploadItem(item, blob) {
      var self = this;
      if (!this.cfg.cloudName || !this.cfg.uploadPreset) {
        item.failed = true;
        this.showHint('Image upload is not configured (Cloudinary settings missing).');
        this.renderThumbs();
        return;
      }
      item.uploading = true;
      item.failed = false;
      item._blob = blob;
      this.renderThumbs();
      this.updateCtas();

      function finish(ok) {
        item.uploading = false;
        item.failed = !ok;
        if (ok) item._blob = null;
        self.setUploadProgress(null);
        self.renderThumbs();
        self.updateCtas();
      }

      /* step 1 — cropped hexagon (cart thumbnail + print reference) */
      self.setUploadProgress(0, 'Uploading crop');
      self.uploadBlobXHR(blob, 'luxanium-hexagon.jpg',
        function (pct) { self.setUploadProgress(pct, 'Uploading crop'); },
        function (url) {
          if (!url) { console.error('[Lux] crop upload failed'); finish(false); return; }
          item.cloudUrl = url;

          /* step 2 — original (print master + dispute record) */
          var orig = item.originalFile;
          if (!orig) { finish(true); return; }
          if (orig.size >= CLD_CAP) self.setUploadProgress('prep', 'Preparing original');
          self.prepareForUpload(orig, function (prepared) {
            self.setUploadProgress(0, 'Uploading original');
            self.uploadBlobXHR(prepared, 'luxanium-original.jpg',
              function (pct) { self.setUploadProgress(pct, 'Uploading original'); },
              function (origUrl) {
                item.originalUrl = origUrl || null;
                item.originalFile = null;
                finish(true);
              });
          });
        });
    }

    retryItem(index) {
      var it = this.items[index];
      if (!it || !it._blob) {
        /* blob gone — rebuild from croppedImg */
        var self = this;
        var c = document.createElement('canvas');
        c.width = EXPORT_W; c.height = EXPORT_H;
        c.getContext('2d').drawImage(it.croppedImg, 0, 0, EXPORT_W, EXPORT_H);
        c.toBlob(function (blob) { if (blob) self.uploadItem(it, blob); }, 'image/jpeg', 0.92);
        return;
      }
      this.uploadItem(it, it._blob);
    }

    /* ================= items / thumbnails ================= */
    setActive(index) {
      this.activeIndex = index;
      this.updateFrontTexture();
      this.renderThumbs();
      if (this.editBtn) this.editBtn.hidden = this.activeIndex < 0;
      this.uploadBtn.classList.toggle('is-hidden', this.items.length > 0);
      this.updateCtas();
    }

    removeItem(index) {
      var it = this.items[index];
      if (!it) return;
      if (it.previewUrl) URL.revokeObjectURL(it.previewUrl);
      this.items.splice(index, 1);
      if (this.activeIndex >= this.items.length) this.activeIndex = this.items.length - 1;
      this.setActive(this.activeIndex);
    }

    onThumbsClick(e) {
      var removeBtn = e.target.closest('[data-thumb-remove]');
      if (removeBtn) {
        e.stopPropagation();
        this.removeItem(parseInt(removeBtn.getAttribute('data-thumb-remove'), 10));
        return;
      }
      var retryBtn = e.target.closest('[data-thumb-retry]');
      if (retryBtn) {
        e.stopPropagation();
        this.retryItem(parseInt(retryBtn.getAttribute('data-thumb-retry'), 10));
        return;
      }
      var addTile = e.target.closest('[data-thumb-add]');
      if (addTile) { this.fileInput.click(); return; }
      var thumb = e.target.closest('[data-thumb-index]');
      if (thumb) {
        var idx = parseInt(thumb.getAttribute('data-thumb-index'), 10);
        if (idx === this.activeIndex) this.openEditorFor(idx);
        else this.setActive(idx);
      }
    }

    renderThumbs() {
      if (!this.thumbsWrap) return;
      var html = '';
      var self = this;

      this.items.forEach(function (it, i) {
        html +=
          '<div class="lux-hex-thumb' + (i === self.activeIndex ? ' is-active' : '') +
            (it.failed ? ' is-failed' : '') + '" data-thumb-index="' + i + '" title="' +
            (i === self.activeIndex ? 'Click to edit' : 'Click to preview') + '">' +
            '<div class="lux-hex-thumb__shape" style="background-image:url(' + it.previewUrl + ')"></div>' +
            (it.uploading ? '<span class="lux-hex-thumb__spinner"></span>' : '') +
            (it.failed
              ? '<button type="button" class="lux-hex-thumb__retry" data-thumb-retry="' + i + '" title="Upload failed — retry">&#8635;</button>'
              : '') +
            '<button type="button" class="lux-hex-thumb__remove" data-thumb-remove="' + i + '" aria-label="Remove image">&times;</button>' +
          '</div>';
      });

      html +=
        '<button type="button" class="lux-hex-thumb lux-hex-thumb--add" data-thumb-add aria-label="Add more images">' +
          '<span>+</span>' +
        '</button>';

      this.thumbsWrap.innerHTML = html;

      if (this.thumbCount) {
        this.thumbCount.textContent = this.items.length > 0
          ? this.items.length + ' image(s) - click + to add more'
          : 'Click + to add images one at a time';  
      }
    }

    updateCtas() {
      var anyUploading = this.items.some(function (it) { return it.uploading; });
      var anyFailed = this.items.some(function (it) { return it.failed; });
      this.continueBtn.disabled = this.items.length === 0 || anyUploading;
      this.continueBtn.classList.toggle('is-loading', anyUploading);
      if (anyFailed) {
        this.showHint('An image failed to upload — click ↻ on its thumbnail to retry, or remove it.');
      }
      this.updateTotalLine();
    }

    updateTotalLine() {
      if (!this.totalEl) return;
      var n = this.items.length;
      if (n === 0 || !this.cfg.unitPrice) {
        this.totalEl.hidden = true;
        return;
      }
      var cur = this.cfg.currency;
      var unit = this.cfg.unitPrice;
      var total = unit * n;
      var fmt = function (v) {
        return cur + v.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      };
      var text = n + ' hexagon' + (n > 1 ? 's' : '') + ' × ' + fmt(unit) + ' = ' + fmt(total);
      if (this.cfg.totalNote) text += ' (' + this.cfg.totalNote + ')';
      this.totalEl.textContent = text;
      this.totalEl.hidden = false;
    }

    /* ================= hand-off to the wall builder ================= */
    continueToWall() {
      if (this.items.length === 0) {
        this.showHint('Please upload at least one image first.');
        return;
      }
      if (this.items.some(function (it) { return it.uploading; })) {
        this.showHint('Your images are still uploading — one moment…');
        return;
      }
      var ready = this.items.filter(function (it) { return it.cloudUrl; });
      if (ready.length !== this.items.length) {
        this.showHint('Some images failed to upload. Retry or remove them before continuing.');
        return;
      }
      try {
        var payload = ready.map(function (it) {
          return {
            imageUrl: it.cloudUrl,
            originalUrl: it.originalUrl || '',
            dims: it.dims || '',
            lowRes: !!it.lowRes
          };
        });
        localStorage.setItem('PendingCustomHexItems', JSON.stringify(payload));
      } catch (e) {
        this.showHint('Could not save your images for the builder. Please try again.');
        return;
      }
      window.location.href = this.cfg.builderUrl;
    }

    /* ================= Three.js hexagon sheet ================= */
    initThree() {
      if (typeof THREE === 'undefined') { this.showHint(''); return; }
      var self = this;
      var w = this.stage.clientWidth, h = this.stage.clientHeight;

      this.scene = new THREE.Scene();
      this.camera = new THREE.PerspectiveCamera(35, w / h, 0.1, 100);
      this.camera.position.set(0, 0, 4.4);

      this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
      this.renderer.setSize(w, h);
      this.renderer.outputEncoding = THREE.sRGBEncoding;
      this.stage.appendChild(this.renderer.domElement);

      this.scene.add(new THREE.AmbientLight(0xffffff, 1.0));

      this.sheetGroup = new THREE.Group();
      this.scene.add(this.sheetGroup);

      /* --- hexagon geometry: pointy-top, width 1.6 world units --- */
      var W = 1.6, H = W * HEX_RATIO, depth = 0.015;
      var pts = [
        [0, H / 2], [W / 2, H / 4], [W / 2, -H / 4],
        [0, -H / 2], [-W / 2, -H / 4], [-W / 2, H / 4]
      ];
      var shape = new THREE.Shape();
      shape.moveTo(pts[0][0], pts[0][1]);
      for (var i = 1; i < pts.length; i++) shape.lineTo(pts[i][0], pts[i][1]);
      shape.closePath();

      /* edge/body — extruded hexagon, brushed edge tone */
      var edgeGeo = new THREE.ExtrudeGeometry(shape, { depth: depth, bevelEnabled: false });
      edgeGeo.translate(0, 0, -depth / 2);
      var edgeMat = new THREE.MeshBasicMaterial({ color: 0xd9d5cd });
      this.sheetGroup.add(new THREE.Mesh(edgeGeo, edgeMat));

      /* front + back faces with bounding-box UV remap */
      function faceGeo() {
        var g = new THREE.ShapeGeometry(shape);
        var uv = g.attributes.uv, pos = g.attributes.position;
        for (var j = 0; j < pos.count; j++) {
          uv.setXY(j, pos.getX(j) / W + 0.5, pos.getY(j) / H + 0.5);
        }
        uv.needsUpdate = true;
        return g;
      }

      /* front texture: canvas composite of mockup + active user image */
      this.frontCanvas = document.createElement('canvas');
      this.frontCanvas.width = 1024;
      this.frontCanvas.height = Math.round(1024 * HEX_RATIO);
      this.frontTex = new THREE.CanvasTexture(this.frontCanvas);
      this.frontTex.encoding = THREE.sRGBEncoding;
      this.frontTex.minFilter = THREE.LinearFilter;
      this.frontTex.magFilter = THREE.LinearFilter;

      var frontMesh = new THREE.Mesh(faceGeo(),
        new THREE.MeshBasicMaterial({ map: this.frontTex }));
      frontMesh.position.z = depth / 2 + 0.001;
      this.sheetGroup.add(frontMesh);

      /* back face */
      this.backCanvas = document.createElement('canvas');
      this.backCanvas.width = 1024;
      this.backCanvas.height = Math.round(1024 * HEX_RATIO);
      this.backTex = new THREE.CanvasTexture(this.backCanvas);
      this.backTex.encoding = THREE.sRGBEncoding;
      var backMesh = new THREE.Mesh(faceGeo(),
        new THREE.MeshBasicMaterial({ map: this.backTex }));
      backMesh.position.z = -depth / 2 - 0.001;
      backMesh.rotation.y = Math.PI;
      this.sheetGroup.add(backMesh);

      /* subtle gloss sweep, hexagon-shaped, additive (never darkens) */
      var glossCanvas = document.createElement('canvas');
      glossCanvas.width = 512; glossCanvas.height = 512;
      var gctx = glossCanvas.getContext('2d');
      var grad = gctx.createLinearGradient(0, 0, 512, 512);
      grad.addColorStop(0.35, 'rgba(255,255,255,0)');
      grad.addColorStop(0.5, 'rgba(255,255,255,0.5)');
      grad.addColorStop(0.65, 'rgba(255,255,255,0)');
      gctx.fillStyle = grad;
      gctx.fillRect(0, 0, 512, 512);
      var glossTex = new THREE.CanvasTexture(glossCanvas);
      glossTex.wrapS = THREE.RepeatWrapping;
      this.gloss = new THREE.Mesh(faceGeo(), new THREE.MeshBasicMaterial({
        map: glossTex, transparent: true, opacity: 0,
        depthWrite: false, blending: THREE.AdditiveBlending
      }));
      this.gloss.position.z = depth / 2 + 0.002;
      this.sheetGroup.add(this.gloss);

      /* load mockups */
      this.mockFrontImg = null;
      this.mockBackImg = null;
      this.loadMockup(this.cfg.frontMockup, function (img) {
        self.mockFrontImg = img;
        self.updateFrontTexture();
      });
      this.loadMockup(this.cfg.backMockup, function (img) {
        self.mockBackImg = img;
        self.updateBackTexture();
      });
      this.updateFrontTexture();
      this.updateBackTexture();

      /* drag-to-rotate with momentum (same feel as the custom page) */
      this.targetRotY = 0;
      this.targetRotX = 0;
      this._drag = null;
      this.intro = { active: true, t: 0, duration: 2200 };

      var el = this.renderer.domElement;
      /* let horizontal drags rotate the sheet smoothly while vertical
         swipes still scroll the page (pan-y). Without this the browser
         hijacks the gesture for scrolling and rotation feels laggy. */
      el.style.touchAction = 'pan-y';
      el.style.webkitUserSelect = 'none';
      el.style.userSelect = 'none';
      el.addEventListener('pointerdown', function (e) {
        self.intro.active = false;
        self._drag = { active: true, x: e.clientX, y: e.clientY, vel: 0, velY: 0 };
        el.setPointerCapture(e.pointerId);
      });
      el.addEventListener('pointermove', function (e) {
        if (!self._drag || !self._drag.active) return;
        var dx = (e.clientX - self._drag.x) * 0.008;
        var dy = (e.clientY - self._drag.y) * 0.006;
        self.targetRotY += dx;
        self.targetRotX = clamp(self.targetRotX + dy, -0.75, 0.75);
        self._drag.vel = dx;
        self._drag.velY = dy;
        self._drag.x = e.clientX;
        self._drag.y = e.clientY;
      });
      el.addEventListener('pointerup', function () {
        if (self._drag) self._drag.active = false;
      });
      el.addEventListener('pointercancel', function () {
        if (self._drag) self._drag.active = false;
      });

      this.animate();
    }

    loadMockup(url, cb) {
      if (!url) { cb(null); return; }
      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = function () { cb(img); };
      img.onerror = function () { cb(null); };
      img.src = url;
    }

    drawCover(ctx, img, w, h) {
      var s = Math.max(w / img.naturalWidth, h / img.naturalHeight);
      var dw = img.naturalWidth * s, dh = img.naturalHeight * s;
      ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
    }

    updateFrontTexture() {
      if (!this.frontCanvas) return;
      var c = this.frontCanvas, ctx = c.getContext('2d');
      ctx.clearRect(0, 0, c.width, c.height);

      var active = this.activeIndex >= 0 ? this.items[this.activeIndex] : null;
      if (active && active.croppedImg) {
        /* user's cropped hexagon image, full bleed */
        ctx.drawImage(active.croppedImg, 0, 0, c.width, c.height);
      } else if (this.mockFrontImg) {
        this.drawCover(ctx, this.mockFrontImg, c.width, c.height);
      } else {
        ctx.fillStyle = '#e9e6df';
        ctx.fillRect(0, 0, c.width, c.height);
      }
      this.frontTex.needsUpdate = true;
    }

    updateBackTexture() {
      if (!this.backCanvas) return;
      var c = this.backCanvas, ctx = c.getContext('2d');
      ctx.clearRect(0, 0, c.width, c.height);
      if (this.mockBackImg) {
        this.drawCover(ctx, this.mockBackImg, c.width, c.height);
      } else {
        ctx.fillStyle = '#d8d4cc';
        ctx.fillRect(0, 0, c.width, c.height);
      }
      this.backTex.needsUpdate = true;
    }

    animate() {
      var self = this;
      var lastTs = performance.now();
      function loop(ts) {
        self._raf = requestAnimationFrame(loop);
        var dt = ts - lastTs;
        lastTs = ts;

        if (self.intro.active) {
          self.intro.t += dt;
          var p = Math.min(1, self.intro.t / self.intro.duration);
          var amp = 0.6, angle;
          if (p < 0.42) {
            angle = -amp * Math.sin((p / 0.42) * Math.PI);
          } else if (p < 0.80) {
            var q = (p - 0.42) / 0.38;
            angle = amp * Math.sin(q * Math.PI);
          } else {
            var s2 = (p - 0.80) / 0.20;
            angle = (amp * 0.18) * (1 - s2) * Math.cos(s2 * Math.PI * 0.5);
          }
          self.sheetGroup.rotation.y = angle;
          self.targetRotY = angle;
          if (p >= 1) { self.intro.active = false; self.targetRotY = 0; }
        } else {
          if (self._drag && !self._drag.active) {
            if (Math.abs(self._drag.vel) > 0.0005) {
              self.targetRotY += self._drag.vel;
              self._drag.vel *= 0.93;
            }
            if (Math.abs(self._drag.velY) > 0.0005) {
              self.targetRotX = clamp(self.targetRotX + self._drag.velY, -0.75, 0.75);
              self._drag.velY *= 0.93;
            }
          }
          self.sheetGroup.rotation.y += (self.targetRotY - self.sheetGroup.rotation.y) * 0.18;
          self.sheetGroup.rotation.x += (self.targetRotX - self.sheetGroup.rotation.x) * 0.18;
        }

        /* gloss visible when tilted off-front */
        if (self.gloss) {
          var ry = self.sheetGroup.rotation.y;
          var facing = Math.cos(ry);
          var offFront = Math.min(1, Math.abs(ry) / 0.18);
          var vis = Math.max(0, facing) * offFront;
          self.gloss.material.opacity = vis * 0.5;
          self.gloss.material.map.offset.x = (ry * 0.18) % 1;
        }

        if (self.renderer) self.renderer.render(self.scene, self.camera);
      }
      this._raf = requestAnimationFrame(loop);
    }

    onResize() {
      if (this.editor && !this.editor.hidden) this.drawEditor();
      if (!this.renderer) return;
      var w = this.stage.clientWidth, h = this.stage.clientHeight;
      this.camera.aspect = w / h;
      this.camera.updateProjectionMatrix();
      this.renderer.setSize(w, h);
    }

    /* ================= helpers ================= */
    showHint(msg) {
      if (!this.hint) return;
      this.hint.textContent = msg || '';
      this.hint.hidden = !msg;
    }
  }

  if (!customElements.get('lux-hexagon-customizer')) {
    customElements.define('lux-hexagon-customizer', LuxHexagonCustomizer);
  }
})();
